import * as React from "react";

import type { Project } from "@/api/projects";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * Pick the project an in-app card opens in, when the viewer has more than one.
 *
 * `projects` is already the valid set (what `GET /api/projects` returned, in
 * the link charset, ordered by name) and `preselected` one of them: the
 * viewer's last-used project, else the first. The list is a native radio
 * group, so arrow keys move the choice and Tab reaches "Open"; Enter submits.
 * "Open" hands the chosen slug back — the caller builds the link
 * (`inAppHref`) and navigates, so this dialog never holds a path.
 */
export function ProjectChooserDialog({
  open,
  onOpenChange,
  appName,
  projects,
  preselected,
  onOpenProject,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appName: string;
  projects: readonly Project[];
  preselected: string;
  onOpenProject: (slug: string) => void;
}): JSX.Element {
  const [selected, setSelected] = React.useState(preselected);
  const [wasOpen, setWasOpen] = React.useState(open);
  const name = React.useId();

  // Every opening starts from the current preselection, not from whatever
  // was picked the last time the dialog closed without opening. Only the
  // opening resets it: a preselection that changes while the dialog is open
  // (a refetch, another tab) never overwrites the viewer's pick.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSelected(preselected);
  }

  const onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onOpenProject(selected);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{`Open ${appName} in a project`}</DialogTitle>
            <DialogDescription>
              {`${appName} runs inside the project you pick. Its runs and artifacts land there.`}
            </DialogDescription>
          </DialogHeader>
          <fieldset className="flex flex-col gap-2">
            <legend className="sr-only">Project</legend>
            {projects.map((project) => (
              <label
                key={project.slug}
                className={cn(
                  "grid min-h-11 cursor-pointer grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3",
                  "rounded-md border px-3 py-2",
                  "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
                  selected === project.slug ? "border-primary bg-primary/5" : "border-border-subtle"
                )}
              >
                <input
                  type="radio"
                  name={name}
                  value={project.slug}
                  checked={selected === project.slug}
                  onChange={() => setSelected(project.slug)}
                  className="row-span-2 h-4 w-4 accent-primary"
                />
                <span className="text-sm font-medium text-foreground">{project.display_name}</span>
                <span className="truncate font-mono text-xs text-muted-foreground">
                  {project.slug}
                </span>
              </label>
            ))}
          </fieldset>
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit">Open</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
